import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LogoImage from '../common/LogoImage';
import './Auth.css';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { password, confirmPassword } = formData;

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    setMessage('');
    
    // Validate passwords match and meet minimum requirements
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    
    if (password.length < 8) {
      return setError('Password must be at least 8 characters long');
    }
    
    setLoading(true);

    try {
      const res = await axios.post(`/api/auth/reset-password/${token}`, { password });
      setMessage(res.data.msg);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#FFFAFA',
      padding: '20px'
    },
    logo: {
      marginBottom: '30px',
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      padding: '30px',
      width: '100%',
      maxWidth: '450px'
    },
    title: {
      color: '#243444',
      textAlign: 'center',
      marginBottom: '20px',
      fontWeight: 'bold'
    },
    alert: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    success: {
      backgroundColor: '#d4edda',
      color: '#155724',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      color: '#4C5454',
      fontWeight: 'bold'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '16px'
    },
    button: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s'
    },
    buttonHover: {
      backgroundColor: '#1a2733'
    },
    link: {
      color: '#243444',
      textDecoration: 'none',
      fontWeight: 'bold'
    },
    footer: {
      textAlign: 'center',
      marginTop: '20px',
      color: '#4C5454'
    },
    info: {
      marginBottom: '20px',
      lineHeight: '1.6',
      color: '#4C5454'
    },
    requirements: {
      fontSize: '14px',
      color: '#4C5454',
      marginTop: '5px',
      marginBottom: '15px'
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.logo}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <LogoImage 
            type="transparent" 
            style={{ maxWidth: '100%', height: '150px' }}
          />
          <h2 style={{ color: '#243444', textAlign: 'center', marginTop: '10px' }}>Business Resilience Navigator</h2>
        </Link>
      </div>
      <div style={styles.card}>
        <h1 style={styles.title}>Set New Password</h1>
        
        {message ? (
          <div style={styles.success}>
            {message}
            <p style={{ marginTop: '10px' }}>Redirecting to login page...</p>
          </div>
        ) : (
          <>
            <p style={styles.info}>
              Enter your new password below.
            </p>
            {error && <div style={styles.alert}>{error}</div>}
            <form onSubmit={onSubmit}>
              <div style={styles.formGroup}>
                <label htmlFor="password" style={styles.label}>New Password</label>
                <input
                  type="password"
                  name="password"
                  id="password"
                  value={password}
                  onChange={onChange}
                  required
                  minLength="8"
                  style={styles.input}
                />
                <p style={styles.requirements}>
                  Password must be at least 8 characters long
                </p>
              </div>
              <div style={styles.formGroup}>
                <label htmlFor="confirmPassword" style={styles.label}>Confirm Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={onChange}
                  required
                  style={styles.input}
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                style={styles.button}
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
            </form>
          </>
        )}
        
        <p style={styles.footer}>
          <Link to="/login" style={styles.link}>Back to Login</Link>
        </p>
      </div>
      <div style={{ marginTop: '30px', textAlign: 'center', color: '#4C5454', fontSize: '14px' }}>
        <Link to="/" style={{ color: '#243444', textDecoration: 'none', display: 'block', marginBottom: '10px' }}>← Back to Home</Link>
        © {new Date().getFullYear()} Business Resilience Navigator. All rights reserved.
      </div>
    </div>
  );
};

export default ResetPassword;
